<div class="wrapper">
  <div class="management-heading">
    <div>
      <h2 class="mat-subtitle-1">Drawings</h2>
      <p class="mat-body-1">Issued sheets for this project. Upload a PDF set to add or revise sheets.</p>
      @if (canPublish && asBuiltOpen > 0) {
        <button type="button" class="waiting-link mat-body-1" data-testid="drawings-as-built-waiting" (click)="showWaiting()">
          {{ asBuiltOpen }} as-built {{ asBuiltOpen === 1 ? 'mark' : 'marks' }} waiting for review</button>
      }
    </div>
    <div class="management-actions">
      @if (exportUrl) {
        <a class="link-button mat-body-1" data-testid="drawings-export-link" [href]="exportUrl" target="_blank" rel="noopener">Download export</a>
      }
      <app-secondary-button buttonId="drawings-export-set" [isLoading]="exporting" [isDisabled]="exporting" (handleClick)="chooseExport()">Export</app-secondary-button>
      @if (canConfigure) {
        <app-primary-button buttonId="drawings-upload-set" data-testid="drawings-upload-set" (handleClick)="upload()">Upload set</app-primary-button>
      }
    </div>
  </div>

  <nav class="management-tabs" aria-label="Drawing lists">
    <button type="button" class="mat-body-1" [class.active]="activeTab === 'sheets'" [attr.aria-current]="activeTab === 'sheets' ? 'page' : null" (click)="setTab('sheets')">Sheets <span>{{ totalRecords }}</span></button>
    <button type="button" class="mat-body-1" data-testid="drawings-tab-as-built" [class.active]="activeTab === 'as-built'" [attr.aria-current]="activeTab === 'as-built' ? 'page' : null"
      (click)="setTab('as-built')">As-built@if (asBuiltTotal !== null) { <span>{{ asBuiltTotal }}</span>}</button>
    @if (canConfigure) {
      <button type="button" class="mat-body-1" [class.active]="activeTab === 'pending'" [attr.aria-current]="activeTab === 'pending' ? 'page' : null" (click)="setTab('pending')">Pending <span>{{ pendingSets.length }}</span></button>
    }
  </nav>

  @if (activeTab === 'sheets') {
    <!-- The RFIs / Submittals / Payapp list toolbar: filter pills, toggle pills and the 44px search pill (W44) -->
    <div class="list-toolbar drawing-filters">
      <!-- W53: the app's Filter (N) (multi-section filter, as on the Issues tab): Discipline, Status and Type, several each -->
      <app-project-details-page-multi-section-filter data-testid="drawings-filter" [sections]="filterSections" (selectionChange)="onFilterChange($event)" />
      <!-- W68: Sheets here is a field feature (mobile only); At my station stays -->
      @if (alignments.length) {
        <button type="button" class="toggle-pill" data-testid="drawings-at-station" [class.toggle-pill--on]="!!atStation" [class.toggle-pill--busy]="locatingStation"
          [attr.aria-pressed]="!!atStation" [title]="locatingStation ? 'Finding you… Click to cancel' : ''" (click)="toggleAtStation()">
          <mat-icon>straighten</mat-icon>{{ locatingStation ? 'Finding you… Cancel' : 'At my station' }}
        </button>
      }
      <div class="search" data-testid="drawings-search">
        <mat-icon svgIcon="search"></mat-icon>
        <input id="drawings-search" type="text" class="search-input" maxlength="256" placeholder="Search number, title or STA 1018+50" aria-label="Search sheets"
          [formControl]="search" />
        @if (search.value) {
          <mat-icon svgIcon="close" role="button" aria-label="Clear the search" data-testid="drawings-search-clear" (click)="clearSearch()"></mat-icon>
        }
      </div>
      <!-- W57: Group by, a view option beside Grid / List (only the groupings that make two groups or more). The toolbar's pill
           dropdown (Figma 11244:33965: label, chevron on the right), the same filter pill as As-built's Sheet / Revision -->
      <mat-form-field class="filter-pill" [class.filter-pill--on]="groupBy !== 'none'" data-testid="drawings-group-by"
        [matTooltip]="groupingSuspended" [matTooltipDisabled]="!groupingSuspended">
        <mat-select [value]="groupBy" (selectionChange)="setGroupBy($event.value)" [disabled]="!!groupingSuspended" disableOptionCentering
          panelClass="rfi-sort-panel" aria-label="Group by">
          <mat-select-trigger>Group by: {{ groupByLabel }}</mat-select-trigger>
          @for (o of groupOptions; track o.value) {
            <mat-option [value]="o.value">{{ o.label }}</mat-option>
          }
        </mat-select>
        <mat-icon matSuffix class="pill-chevron" svgIcon="chevron-down" aria-hidden="true" />
      </mat-form-field>
      <div class="view-toggle" role="group" aria-label="Layout">
        <button type="button" [class.active]="viewMode === 'grid'" [attr.aria-pressed]="viewMode === 'grid'" (click)="viewMode = 'grid'">Grid</button>
        <button type="button" [class.active]="viewMode === 'list'" [attr.aria-pressed]="viewMode === 'list'" (click)="viewMode = 'list'">List</button>
      </div>
    </div>

    @if (searchStation) {
      <div class="applied-row">
        <span class="applied-chip" data-testid="drawings-station-search">Sheets at {{ searchStation.label }}
          <button type="button" aria-label="Clear the station search" data-testid="drawings-station-search-clear" (click)="clearSearch()"><mat-icon>close</mat-icon></button>
        </span>
      </div>
    }

    @if (recent.length && !otherFilters && !atStation) {
      <div class="recent-row" data-testid="drawings-recent">
        <span class="recent-label mat-caption">Recent</span>
        @for (r of recent; track r.uuid) {
          <button type="button" class="recent-chip" data-testid="drawings-recent-sheet" [matTooltip]="r.title || ''" [matTooltipDisabled]="!r.title"
            (click)="open(r.uuid)"><b>{{ r.number }}</b>@if (r.title) { <span>{{ r.title }}</span> }</button>
        }
      </div>
    }

    @if (stationHint) {
      <p class="here-hint mat-body-1" role="status" data-testid="drawings-station-hint">{{ stationHint }}</p>
    } @else if (atStation) {
      <div class="here-hint mat-body-1 ph-no-capture" role="status" data-sentry-block data-testid="drawings-station-on">
        <span>{{ atStationText() }}, where you were at {{ stationTakenAt | date: 'h:mm a' }}.</span>
        @if (alignments.length > 1) {
          <label class="station-alignment">Alignment
            <select class="filter-input" data-testid="drawings-station-alignment" [value]="stationAlignment" (change)="chooseStationAlignment($any($event.target).value)">
              <option value="">Nearest</option>
              @for (a of alignments; track $index) { <option [value]="a.name">{{ a.name || 'Unnamed' }}</option> }
            </select>
          </label>
        }
        <button type="button" class="link-button" (click)="toggleAtStation()">Show all sheets</button>
      </div>
      @if (atStation.at.stationing === 'chainage') {
        <p class="here-hint station-note mat-caption" data-testid="drawings-station-chainage">Stations measured along the line; plan stations unavailable</p>
      }
    }
    @if (loadError && !loading) {
      <!-- Audit 1: the list failed to load: said here with Retry, not only in the toast (any rows shown are the last good answer) -->
      <p class="here-hint mat-body-1 load-error" role="alert" data-testid="drawings-load-error">Could not load drawings.
        <button type="button" class="link-button" data-testid="drawings-load-retry" (click)="load()">Retry</button></p>
    }
    @if (loading && !rows.length) {
      <p role="status">Loading drawings…</p>
    } @else if (!rows.length && searchStation) {
      <div class="empty-state" data-testid="drawings-station-search-empty">
        <h3 class="mat-subtitle-1">No sheet at {{ searchStation.label }}</h3>
        <p>No sheet's station range covers {{ searchStation.label }}@if (activeFilters.discipline.length || activeFilters.status.length || activeFilters.type.length) { with these filters}.</p>
      </div>
    } @else if (!rows.length && atStation) {
      <div class="empty-state" data-testid="drawings-station-empty">
        <h3 class="mat-subtitle-1">No sheet at this station</h3>
        @if (otherFilters) {
          <p>No sheet at your station matches the other filters. Clear them to see every sheet at this station.</p>
        } @else {
          <p>No sheet's station range covers where you are. Sheets show here once their stations are set.</p>
        }
      </div>
    } @else if (!rows.length) {
      <div class="empty-state">
        <h3 class="mat-subtitle-1">No drawings yet</h3>
        <p>Upload a PDF set to start the register.</p>
      </div>
    } @else if (viewMode === 'grid') {
      <ng-template #sheetCard let-row>
          <div class="drawing-card" role="button" tabindex="0" [attr.data-testid]="'drawings-sheet-card-' + row.number" [attr.aria-label]="cardLabel(row)" [matTooltip]="row.title" (click)="open(row.uuid)" (keydown.enter)="open(row.uuid)">
            <div class="thumb">
              @if (row.current?.thumb_url) {
                <img [src]="row.current.thumb_url" [alt]="row.number + ' thumbnail'" loading="lazy" />
              } @else {
                <span class="thumb-placeholder mat-caption">No preview</span>
              }
              <button type="button" mat-icon-button class="card-menu" [matMenuTriggerFor]="cardMenu" [attr.aria-label]="'Options for ' + row.number" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()"><mat-icon>more_horiz</mat-icon></button>
              <mat-menu #cardMenu="matMenu" class="tab-more-menu">
                <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: row }" />
              </mat-menu>
            </div>
            <div class="card-number">{{ row.number }}</div>
            @if (subtitle(row); as line) { <div class="card-subtitle mat-body-1">{{ line }}</div> }
            <div class="chip-row">
              @if (row.discipline) { <span class="tag-chip" data-testid="drawings-card-discipline">{{ row.discipline }}</span> }
              <!-- W52: the revision, or why there is none, as a neutral status badge (Figma 32911:76232) -->
              <span class="status-badge" data-testid="drawings-card-rev">{{ row.current ? 'Rev ' + (row.current.label ?? '—') : statusLabel(row) }}</span>
            </div>
          </div>
      </ng-template>
      @if (grouped) {
        @for (group of groups; track group.key) {
          <div class="group">
            <ng-container *ngTemplateOutlet="groupHeader; context: { $implicit: group }" />
            @if (group.expanded) {
              <div class="card-grid">
                @for (row of group.rows ?? []; track row.uuid) { <ng-container *ngTemplateOutlet="sheetCard; context: { $implicit: row }" /> }
              </div>
              <ng-container *ngTemplateOutlet="groupMore; context: { $implicit: group }" />
            }
          </div>
        }
      } @else {
        <div class="card-grid">
          @for (row of flatRows; track row.uuid) { <ng-container *ngTemplateOutlet="sheetCard; context: { $implicit: row }" /> }
        </div>
      }
    } @else {
      <div class="table-scroll-wrapper">
        <table mat-table [dataSource]="listItems" class="table">
          <ng-container matColumnDef="group"><td mat-cell *matCellDef="let item" [attr.colspan]="displayedColumns.length" class="group-cell">
            <ng-container *ngTemplateOutlet="groupHeader; context: { $implicit: item.group }" />
          </td></ng-container>
          <ng-container matColumnDef="more"><td mat-cell *matCellDef="let item" [attr.colspan]="displayedColumns.length" class="more-cell">
            <ng-container *ngTemplateOutlet="groupMore; context: { $implicit: item.more }" />
          </td></ng-container>
          <ng-container matColumnDef="thumb"><th mat-header-cell *matHeaderCellDef></th><td mat-cell *matCellDef="let row">
            @if (row.current?.thumb_url) { <img class="row-thumb" [src]="row.current.thumb_url" [alt]="row.number + ' thumbnail'" loading="lazy" /> } @else { <span class="row-thumb row-thumb-empty"></span> }
          </td></ng-container>
          <ng-container matColumnDef="number"><th mat-header-cell *matHeaderCellDef>Number</th><td mat-cell *matCellDef="let row"><span class="mono">{{ row.number }}</span></td></ng-container>
          <ng-container matColumnDef="title"><th mat-header-cell *matHeaderCellDef>Title</th><td mat-cell *matCellDef="let row">{{ row.title }}</td></ng-container>
          <ng-container matColumnDef="discipline"><th mat-header-cell *matHeaderCellDef>Discipline</th><td mat-cell *matCellDef="let row">{{ row.discipline || '—' }}</td></ng-container>
          <ng-container matColumnDef="type"><th mat-header-cell *matHeaderCellDef>Type</th><td mat-cell *matCellDef="let row">{{ typeLabel(row.sheet_type) }}</td></ng-container>
          <ng-container matColumnDef="rev"><th mat-header-cell *matHeaderCellDef>Rev</th><td mat-cell *matCellDef="let row">{{ row.current ? row.current.label : '—' }}</td></ng-container>
          <ng-container matColumnDef="status"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let row">@if (row.current) { <span class="status-badge status-badge--info">Current</span> } @else { <span class="status-badge">Not published</span> }</td></ng-container>
          <ng-container matColumnDef="station"><th mat-header-cell *matHeaderCellDef>Station</th><td mat-cell *matCellDef="let row">{{ stationLabel(row) }}</td></ng-container>
          <ng-container matColumnDef="actions"><th mat-header-cell *matHeaderCellDef></th><td mat-cell *matCellDef="let row" (click)="$event.stopPropagation()">
            <button type="button" mat-icon-button [matMenuTriggerFor]="rowMenu" [attr.aria-label]="'Options for ' + row.number"><mat-icon>more_horiz</mat-icon></button>
            <mat-menu #rowMenu="matMenu" class="tab-more-menu">
              <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: row }" />
            </mat-menu>
          </td></ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let item; columns: ['group']; when: isGroupItem" class="group-row"></tr>
          <tr mat-row *matRowDef="let item; columns: ['more']; when: isMoreItem" class="more-row"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns; when: isSheetItem" [attr.data-testid]="'drawings-sheet-row-' + row.number" (click)="open(row.uuid)"></tr>
        </table>
      </div>
    }

    @if (!grouped && totalRecords > pageSize) {
      <mat-paginator [length]="totalRecords" [pageSize]="pageSize" [pageIndex]="pageIndex" [hidePageSize]="true" (page)="page($event)"></mat-paginator>
    }
  } @else if (activeTab === 'as-built') {
    <app-drawings-as-built [projectId]="projectId" [canPublish]="canPublish" [canConfigure]="canConfigure" [status]="asBuiltStatus"
      [initialSheet]="asBuiltSheet" [initialRevision]="asBuiltRevision" [initialQuery]="asBuiltQuery"
      (openMark)="openMark.emit($event)" (countsChange)="onAsBuiltCounts($event)" (filtersChange)="onAsBuiltFilters($event)" />
  } @else {
    @if (pendingSets.length) {
      <div class="table-scroll-wrapper">
        <table class="table pending-table">
          <thead><tr><th>Set</th><th>Pages</th><th>Status</th><th class="pending-actions"></th></tr></thead>
          <tbody>
            @for (set of pendingSets; track set.uuid) {
              <tr class="pending-row" tabindex="0" (click)="openSet.emit(set.uuid)" (keydown.enter)="openSet.emit(set.uuid)">
                <td>{{ set.name }}
                  @if (set.target_sheet) {
                    <!-- W65: a set made by Upload new revision -->
                    <span class="pending-target mat-caption" data-testid="drawings-pending-target">Revision of {{ set.target_sheet.number }}</span>
                  }
                </td>
                <td>{{ set.revision_count }}</td>
                <td>{{ inspectLabel(set) }}</td>
                <td class="pending-actions" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()">
                  <button type="button" mat-icon-button data-testid="drawings-pending-more" [matMenuTriggerFor]="pendingMenu" [attr.aria-label]="'Options for ' + set.name"><mat-icon>more_horiz</mat-icon></button>
                  <mat-menu #pendingMenu="matMenu" class="tab-more-menu">
                    <button mat-menu-item type="button" data-testid="drawings-pending-discard" (click)="discardSet(set)">Discard set</button>
                  </mat-menu>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="empty-state">Nothing is pending. Choose Upload set to add drawings.</div>
    }
  }
</div>

<!-- W57: a group's header ("Plan · 66"), collapsed until opened, and its Show all -->
<ng-template #groupHeader let-group>
  <button type="button" class="group-header" data-testid="drawings-group" [attr.aria-expanded]="group.expanded" (click)="toggleGroup(group)">
    <mat-icon>{{ group.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon><span class="group-name">{{ group.label }}</span>
    <span class="group-count" data-testid="drawings-group-count">{{ group.count }}</span>
  </button>
</ng-template>
<ng-template #groupMore let-group>
  @if (group.loading) {
    <p class="group-more mat-body-1" role="status">Loading sheets…</p>
  } @else if (group.error) {
    <p class="group-more mat-body-1" role="alert" data-testid="drawings-group-error">These sheets could not be loaded.
      <button type="button" class="link-button" data-testid="drawings-group-retry" (click)="retryGroup(group)">Retry</button></p>
  } @else if (canShowAll(group)) {
    <button type="button" class="link-button group-more mat-body-1" data-testid="drawings-group-show-all" (click)="showAll(group)">Show all {{ group.count }}</button>
  }
</ng-template>

<ng-template #rowActions let-row>
  <button mat-menu-item (click)="open(row.uuid)">Open</button>
  @if (canWithdraw(row)) { <button mat-menu-item (click)="withdraw(row)">Withdraw current revision</button> }
  @if (canConfigure) { <button mat-menu-item (click)="remove(row)">Delete sheet</button> }
</ng-template>
